import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ArrowRight } from 'lucide-react';

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const closeMenu = () => setIsMenuOpen(false);

  const isActive = (path: string) => {
    return location.pathname === path ? "text-primary-400 font-bold" : "text-gray-400 hover:text-white font-medium";
  };

  return (
    <header className="fixed w-full top-0 z-50 bg-gray-950/95 md:bg-gray-950/80 backdrop-blur-2xl border-b border-white/[0.06] transition-all duration-300 shadow-[0_4px_24px_-8px_rgba(0,0,0,0.5)] md:shadow-[0_10px_40px_-15px_rgba(0,0,0,0.7)]">
      {/* Subtle gradient fade at bottom on mobile - no harsh black gap */}
      <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-primary-500/20 to-transparent pointer-events-none" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-3 md:py-4">
          <Link to="/" className="flex items-center group shrink-0 min-w-0" onClick={closeMenu}>
            <img
              src="/logo/logo-white.png"
              alt="APDigitalHub - High-Performance Digital Marketing Agency 🇨🇦 Canada"
              className="h-10 w-auto max-w-[180px] object-contain object-left transition-all duration-300 group-hover:scale-105 sm:h-12 md:h-14"
            />
          </Link>

          <nav className="hidden lg:flex space-x-10">
            <Link to="/" className={`${isActive('/')} text-xs uppercase tracking-widest transition duration-200`}>Home</Link>
            <Link to="/services" className={`${isActive('/services')} text-xs uppercase tracking-widest transition duration-200`}>Services</Link>
            <Link to="/portfolio" className={`${isActive('/portfolio')} text-xs uppercase tracking-widest transition duration-200`}>Portfolio</Link>
            <Link to="/blog" className={`${isActive('/blog')} text-xs uppercase tracking-widest transition duration-200`}>Insights</Link>
            <Link to="/contact" className={`${isActive('/contact')} text-xs uppercase tracking-widest transition duration-200`}>Contact</Link>
          </nav>

          <div className="hidden md:flex items-center space-x-3">
            <Link to="/book?type=review">
              <button className="px-5 py-2.5 text-[11px] font-black uppercase tracking-widest text-gray-300 bg-white/[0.03] hover:bg-white/[0.08] hover:text-white border border-white/10 rounded-xl transition-all">
                Free Consultation
              </button>
            </Link>
            <Link to="/book?type=consultation">
              <button className="group relative px-5 py-2.5 bg-primary-600 text-white font-bold rounded-xl overflow-hidden transition-all hover:scale-[1.05] active:scale-95 shadow-[0_10px_20px_-10px_rgba(37,99,235,0.4)]">
                <span className="relative z-10 flex items-center text-[11px] uppercase tracking-widest">
                  Contact Us
                  <ArrowRight className="ml-2 h-3.5 w-3.5 group-hover:translate-x-1 transition-transform" />
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-primary-500 to-blue-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </button>
            </Link>
          </div>

          <div className="md:hidden flex items-center shrink-0">
            <button onClick={toggleMenu} className="p-2.5 text-gray-400 hover:text-white transition-colors bg-white/5 hover:bg-white/10 rounded-xl border border-white/10" aria-label="Toggle menu">
              {isMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

        {isMenuOpen && (
          <div className="md:hidden">
            <div className="px-4 pt-4 pb-8 space-y-2 bg-gray-950/95 backdrop-blur-3xl rounded-[2rem] border border-white/[0.05] mb-6 animate-fade-in shadow-2xl">
              <Link to="/" className={`block px-5 py-4 rounded-2xl text-lg ${isActive('/')}`} onClick={closeMenu}>Home</Link>
              <Link to="/services" className={`block px-5 py-4 rounded-2xl text-lg ${isActive('/services')}`} onClick={closeMenu}>Services</Link>
              <Link to="/portfolio" className={`block px-5 py-4 rounded-2xl text-lg ${isActive('/portfolio')}`} onClick={closeMenu}>Portfolio</Link>
              <Link to="/blog" className={`block px-5 py-4 rounded-2xl text-lg ${isActive('/blog')}`} onClick={closeMenu}>Insights</Link>
              <Link to="/contact" className={`block px-5 py-4 rounded-2xl text-lg ${isActive('/contact')}`} onClick={closeMenu}>Contact</Link>
              <div className="flex flex-col space-y-3 pt-6">
                <Link to="/book?type=consultation" onClick={closeMenu}>
                  <button className="w-full py-4 text-xs font-black uppercase tracking-widest text-white bg-white/5 border border-white/10 rounded-2xl">Free Consultation</button>
                </Link>
                <Link to="/book?type=consultation" onClick={closeMenu}>
                  <button className="w-full py-4 text-xs font-black uppercase tracking-widest text-white bg-primary-600 rounded-2xl shadow-xl">Contact Us</button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;